import { createPortal } from "react-dom";
import { useUserData } from "features/userData/context";
import { _ } from "i18n";
import { UserRole } from "types";
import ClubModal from "pages/productManagement/nonPartnerManagement/nonPartnerClub/ClubModal";
import { ProgramGameModalForm } from "./ProgramGameModalForm";
import { useProgramGameForm } from "./useProgramGameForm";
import { useProgramGameSelectOptions } from "./useProgramGameSelectOptions";
import { useProgramGameClubCreation } from "./hooks/useProgramGameClubCreation";
import { useNonPartnerClubReferenceData } from "./hooks/useNonPartnerClubReferenceData";
import type { DiscoverGamesProgramGameModalProps, OwnershipOption } from "./types";
import "./DiscoverGamesProgramGameModal.css";

export function DiscoverGamesProgramGameModal({
	isOpen,
	onClose,
	ownershipOptions,
}: DiscoverGamesProgramGameModalProps) {
	const user = useUserData();
	const isAdminUser = user.roles.includes(UserRole.Admin);
	const ownershipItems = ownershipOptions as OwnershipOption[];

	const { typeOptions, nationalOptions, priorityOptions, typesLoading } =
		useProgramGameSelectOptions(isOpen);

	const formState = useProgramGameForm({
		isOpen,
		isAdminUser,
		ownershipItems,
		typeOptions,
		onClose,
	});

	const clubCreation = useProgramGameClubCreation({ updateForm: formState.updateForm });
	const referenceData = useNonPartnerClubReferenceData(isOpen && clubCreation.canCreateClub);

	if (!isOpen) {
		return null;
	}

	return createPortal(
		<>
			<div className="discoverGamesProgramModal-overlay" role="presentation" onClick={onClose}>
				<div
					className="discoverGamesProgramModal"
					role="dialog"
					aria-modal="true"
					aria-labelledby="discoverGamesProgramModal-title"
					onClick={(event) => event.stopPropagation()}
				>
					<div className="discoverGamesProgramModal-header">
						<h2 id="discoverGamesProgramModal-title" className="discoverGamesProgramModal-title">
							{_("discover.gamesTable.programGame.modalTitle")}
						</h2>
						<button
							type="button"
							className="discoverGamesProgramModal-closeBtn"
							onClick={onClose}
							aria-label={_("discover.gamesTable.programGame.closeAria")}
						>
							×
						</button>
					</div>

					<ProgramGameModalForm
						form={formState.form}
						updateForm={formState.updateForm}
						isSubmitted={formState.isSubmitted}
						isSubmitting={formState.isSubmitting}
						isAdminUser={isAdminUser}
						formErrors={formState.formErrors}
						homeTeamOptions={formState.homeTeamOptions}
						awayTeamOptions={formState.awayTeamOptions}
						homeTeamsLoading={formState.homeTeamsLoading}
						awayTeamsLoading={formState.awayTeamsLoading}
						homeClubHasNoTeams={formState.homeClubHasNoTeams}
						awayClubHasNoTeams={formState.awayClubHasNoTeams}
						loadHomeOptions={formState.loadHomeOptions}
						loadAwayOptions={formState.loadAwayOptions}
						typeOptions={typeOptions}
						nationalOptions={nationalOptions}
						nationalLeagueOptions={formState.nationalLeagueOptions}
						nationalLeaguesLoading={formState.nationalLeaguesLoading}
						isNationalSelected={formState.isNationalSelected}
						competitionEditionOptions={formState.competitionEditionOptions}
						competitionEditionsLoading={formState.competitionEditionsLoading}
						canCreateClub={clubCreation.canCreateClub}
						onRequestCreateClub={clubCreation.requestCreate}
						priorityOptions={priorityOptions}
						typesLoading={typesLoading}
						ownershipItems={ownershipItems}
						handleOwnershipChange={formState.handleOwnershipChange}
						setTeamsheetLinkAt={formState.setTeamsheetLinkAt}
						setVideoLinkAt={formState.setVideoLinkAt}
						onSubmit={(event) => void formState.handleSubmit(event)}
						onClose={onClose}
					/>
				</div>
			</div>
			{clubCreation.activeSide !== null && referenceData.isReady ? (
				<ClubModal
					club={null}
					initialName={clubCreation.prefillName}
					onClose={clubCreation.close}
					onUpdateClub={clubCreation.onUpdateClub}
					isSubmitted={clubCreation.isSubmittedRef}
					countries={referenceData.countries}
					countriesWithContinents={referenceData.countriesWithContinents}
					geographicalAreaList={referenceData.geographicalAreaList}
				/>
			) : null}
		</>,
		document.body,
	);
}
